import { useEffect, useMemo, useRef, useState } from 'react';
import ElementFace from './ElementFace';
import { X } from 'lucide-react';
import {
  ELEMENTS,
  CATEGORY_COLOR,
  CATEGORY_LABELS,
  normalizeCategory,
  type ElementInfo,
  type ElementCategory,
} from './elements';
import {
  VizCard,
  NowYouTry,
  VIZ_FOCUS,
  DARK_INK,
  DARK_INK_SOFT,
  usePrefersReducedMotion,
  cueIsActive,
  cueHighlights,
  type VizCue,
} from './vizShared';

// InteractivePeriodicTable — full standard-layout table (main groups + d-block +
// the f-block lanthanide/actinide row). Click a cell for a detail popover.
// `config` can spotlight a slide's relevant elements (everything else dims):
//   { highlightCategory?, highlightGroup?, highlightPeriod?, highlightSymbols?, title? }
// Resilient to partial/empty config (no spotlight → all cells full opacity).

export interface PeriodicTableConfig {
  title?: string;
  highlightCategory?: ElementCategory;
  highlightGroup?: number;
  highlightPeriod?: number;
  highlightSymbols?: string[];
}

interface Props {
  config?: PeriodicTableConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

// Grid placement. Main-table elements use their group as the column (1-18). The
// f-block (la/ac, group === null) is laid out in a detached row below, columns
// 3..17, ordered by Z. We compute an explicit (col,row) for every element.
interface Placed extends ElementInfo {
  col: number; // 1-18
  row: number; // 1-7 main, 9-10 for the f-block strip (8 = spacer)
}

const F_BLOCK_START_COL = 3;

/** The range markers that stand in for the f block inside the main grid. The
 *  elements themselves are laid out in the detached rows 9 and 10. */
const F_BLOCK_MARKERS = [
  { row: 6, label: '57-71' },
  { row: 7, label: '89-103' },
];
const ALL_CATEGORIES = new Set<string>(Object.keys(CATEGORY_LABELS));

function placeElements(): Placed[] {
  const placed: Placed[] = [];
  // f-block sequences, laid out left→right from Z order.
  const lanth = ELEMENTS.filter((e) => e.category === 'lanthanide' && e.group === null).sort((a, b) => a.z - b.z);
  const actin = ELEMENTS.filter((e) => e.category === 'actinide' && e.group === null).sort((a, b) => a.z - b.z);

  for (const e of ELEMENTS) {
    if (e.group === null) continue; // handled below
    placed.push({ ...e, col: e.group, row: e.period });
  }
  lanth.forEach((e, idx) => placed.push({ ...e, col: F_BLOCK_START_COL + idx, row: 9 }));
  actin.forEach((e, idx) => placed.push({ ...e, col: F_BLOCK_START_COL + idx, row: 10 }));
  return placed;
}

export default function InteractivePeriodicTable({ config = {}, onComplete, cue }: Props) {
  const reducedMotion = usePrefersReducedMotion();
  const cueOn = cueIsActive(cue);
  const placed = useMemo(placeElements, []);
  const [selected, setSelected] = useState<Placed | null>(null);
  // Self-demo: a gentle sweep highlight that rises then settles across periods.
  const [sweepPeriod, setSweepPeriod] = useState<number | null>(null);
  const [showTry, setShowTry] = useState(false);
  const interactedRef = useRef(false);
  const demoActiveRef = useRef(false);

  const { highlightCategory, highlightGroup, highlightPeriod, highlightSymbols } = config;

  // Cue-driven tokens (symbols, category names, or numeric group/period strings).
  const cueTokens = useMemo(() => cueHighlights(cue), [cue]);
  // Resolve any family names in the cue to real category keys ("noble gases" →
  // "noble-gas") so a spoken family lights up its column/region.
  const cueCategories = useMemo(
    () =>
      cueTokens
        .map((t) => normalizeCategory(t))
        .filter((t): t is string => !!t && ALL_CATEGORIES.has(t)),
    [cueTokens],
  );
  const catKey = normalizeCategory(highlightCategory);
  const catKeyValid = !!catKey && ALL_CATEGORIES.has(catKey);

  const hasSpotlight =
    catKeyValid ||
    highlightGroup != null ||
    highlightPeriod != null ||
    (highlightSymbols != null && highlightSymbols.length > 0) ||
    cueTokens.length > 0;

  const symbolSet = useMemo(
    () => new Set((highlightSymbols ?? []).map((s) => s.toLowerCase())),
    [highlightSymbols],
  );

  const isSpotlit = (e: Placed): boolean => {
    if (!hasSpotlight) return true;
    if (catKeyValid && e.category === catKey) return true;
    if (highlightGroup != null && e.group === highlightGroup) return true;
    if (highlightPeriod != null && e.period === highlightPeriod) return true;
    if (symbolSet.has(e.symbol.toLowerCase())) return true;
    if (cueCategories.includes(e.category)) return true;
    // Cue tokens: symbol or numeric group/period (families handled above).
    for (const tok of cueTokens) {
      if (e.symbol.toLowerCase() === tok) return true;
      const num = Number(tok);
      if (!Number.isNaN(num) && (e.group === num || e.period === num)) return true;
    }
    return false;
  };

  // --- Self-demo: sweep a wave of opacity down the periods, then "Now you try".
  useEffect(() => {
    const demoEnabled = !reducedMotion && !cueOn;
    if (!demoEnabled) {
      setSweepPeriod(null);
      return;
    }
    demoActiveRef.current = true;
    const timers: number[] = [];
    const periods = [1, 2, 3, 4, 5, 6, 7];
    periods.forEach((p, k) => {
      timers.push(window.setTimeout(() => {
        if (demoActiveRef.current) setSweepPeriod(p);
      }, 180 + k * 220));
    });
    timers.push(window.setTimeout(() => {
      if (!demoActiveRef.current) return;
      demoActiveRef.current = false;
      setSweepPeriod(null);
      setShowTry(true);
    }, 180 + periods.length * 220 + 200));
    return () => {
      demoActiveRef.current = false;
      timers.forEach((t) => window.clearTimeout(t));
    };
    // Arm once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cue arriving cancels the demo.
  useEffect(() => {
    if (cueOn) {
      demoActiveRef.current = false;
      setSweepPeriod(null);
      setShowTry(false);
    }
  }, [cueOn]);

  const cancelDemo = () => {
    if (demoActiveRef.current) {
      demoActiveRef.current = false;
      setSweepPeriod(null);
    }
    setShowTry(false);
  };

  const handleSelect = (e: Placed) => {
    cancelDemo();
    setSelected(e);
    if (!interactedRef.current) interactedRef.current = true;
    onComplete?.({ type: 'periodic_table', selected: e.symbol });
  };

  // Categories actually present, for the legend.
  const legendCats = useMemo(() => {
    const set = new Set<ElementCategory>();
    ELEMENTS.forEach((e) => set.add(e.category));
    return Array.from(set);
  }, []);

  return (
    <VizCard
      eyebrow="Periodic table"
      title={config.title}
      reducedMotion={reducedMotion}
      hint="Click an element"
    >
      <div className="relative w-full">
        <NowYouTry show={showTry} />

        {/* The table scrolls horizontally on narrow screens; never grows the page. */}
        <div className="overflow-x-auto pb-1">
          <div
            className="grid gap-[2px]"
            style={{
              gridTemplateColumns: 'repeat(18, minmax(1.5rem, 1fr))',
              gridTemplateRows: 'repeat(7, auto) 0.4rem repeat(2, auto)',
              minWidth: '34rem',
            }}
          >
            {/* The "57–71" / "89–103" markers the textbook prints in group 3.
                Lu and Lr used to sit here as real cells (the IUPAC-2021
                placement), which left the detached rows 14 wide, starting at
                Ce and Th. Every table the students hold — including the course
                slide — uses the older convention: a range marker here and all
                FIFTEEN elements below. A teaching table that disagrees with the
                one in front of the class is just a second thing to explain. */}
            {F_BLOCK_MARKERS.map((mk) => (
              <div
                key={mk.label}
                aria-hidden="true"
                style={{ gridColumn: 3, gridRow: mk.row }}
                className="flex items-center justify-center rounded border border-dashed border-ws-ink-4/50 bg-ws-surface-2 text-[8px] font-semibold leading-none text-ws-ink-3"
                title={`${mk.label} are shown in the row below`}
              >
                {mk.label}
              </div>
            ))}
            {placed.map((e) => {
              const lit = isSpotlit(e);
              // During the sweep demo, the current period gets a brief lift.
              const swept = sweepPeriod != null && e.period === sweepPeriod;
              const opacity = sweepPeriod != null ? (swept ? 1 : 0.5) : lit ? 1 : 0.45;
              return (
                <button
                  key={e.z}
                  type="button"
                  onClick={() => handleSelect(e)}
                  title={`${e.name} (${e.symbol})`}
                  style={{
                    gridColumn: e.col,
                    gridRow: e.row,
                    backgroundColor: CATEGORY_COLOR[e.category],
                    opacity,
                    transform: swept ? 'scale(1.06)' : undefined,
                  }}
                  className={`flex aspect-square min-w-0 flex-col items-center justify-center rounded-[3px] px-0.5 leading-none transition-all duration-300 ease-out hover:z-10 hover:scale-110 hover:shadow ${VIZ_FOCUS} ${
                    selected?.z === e.z ? 'ring-2 ring-ws-accent' : ''
                  } ${lit ? 'hover:ring-1 hover:ring-ws-ink/40' : ''}`}
                >
                  <span className="text-[6px] font-medium" style={{ color: DARK_INK_SOFT }}>{e.z}</span>
                  <span className="text-[10px] font-bold" style={{ color: DARK_INK }}>{e.symbol}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1">
          {legendCats.map((cat) => (
            <div key={cat} className="flex items-center gap-1.5">
              <span
                className="inline-block h-2.5 w-2.5 rounded-[2px]"
                style={{ backgroundColor: CATEGORY_COLOR[cat] }}
              />
              <span className="text-[10px] text-ws-ink-3">{CATEGORY_LABELS[cat]}</span>
            </div>
          ))}
        </div>

        {/* Detail popover */}
        {selected && (
          <div className="mt-3 flex items-start gap-3 rounded-md border border-ws-border bg-ws-surface p-3 shadow-sm">
            <ElementFace
              z={selected.z}
              symbol={selected.symbol}
              name={selected.name}
              mass={selected.mass}
              background={CATEGORY_COLOR[selected.category]}
              ink={DARK_INK}
              inkSoft={DARK_INK_SOFT}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between">
                <h4 className="text-base font-semibold text-ws-ink">{selected.name}</h4>
                <button
                  type="button"
                  onClick={() => setSelected(null)}
                  className={`rounded-sm p-1 text-ws-ink-3 transition-colors hover:bg-ws-surface-2 hover:text-ws-ink ${VIZ_FOCUS}`}
                  aria-label="Close"
                >
                  <X size={14} />
                </button>
              </div>
              <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-ws-ink-2">
                <div><dt className="inline text-ws-ink-3">Atomic no.: </dt><dd className="inline font-medium">{selected.z}</dd></div>
                <div><dt className="inline text-ws-ink-3">Mass: </dt><dd className="inline font-medium">{selected.mass}</dd></div>
                <div><dt className="inline text-ws-ink-3">Category: </dt><dd className="inline font-medium">{CATEGORY_LABELS[selected.category]}</dd></div>
                <div><dt className="inline text-ws-ink-3">Group: </dt><dd className="inline font-medium">{selected.group ?? '—'}</dd></div>
                <div><dt className="inline text-ws-ink-3">Period: </dt><dd className="inline font-medium">{selected.period}</dd></div>
              </dl>
            </div>
          </div>
        )}
      </div>
    </VizCard>
  );
}
